import React from "react";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { useSiteContext } from "../../../Site";
import {
  Container,
  DimensionWrapper,
  DimensionLabel,
  DimensionValue,
  RoofDimensionsBlock,
  RoofIllustrationBlock,
  RoofName,
  SectionTitle,
  classes
} from "./SelectedRoof.styles";
import useRoofData from "./hooks/useRoofData";
import type { SVGImport } from "@bmi-digital/svg-import";
import type { DimensionsValues, RoofV2 as Roof } from "../../types/roof";

type Props = {
  roof: Roof;
  dimensions: DimensionsValues;
};

const SelectedRoof = (props: Props) => {
  const { getMicroCopy } = useSiteContext();

  const {
    roofLengthValues,
    roofAngleValues,
    protrusionLengthValues,
    protrusionAngleValues,
    protrusion
  } = useRoofData({
    dimensions: props.dimensions
  });

  return (
    <>
      <DimensionsSection
        title={getMicroCopy(microCopy.RESULTS_ROOF_SECTION_TITLE)}
        lengthValues={roofLengthValues}
        angleValues={roofAngleValues}
        roofOrProtrusionName={getMicroCopy(microCopy.ROOF_SELECTION_ROOF, {
          name: props.roof.name
        })}
        Illustration={props.roof.illustration}
      />
      {protrusion ? (
        <DimensionsSection
          title={getMicroCopy(microCopy.RESULTS_PROTRUSION_SECTION_TITLE)}
          lengthValues={protrusionLengthValues}
          angleValues={protrusionAngleValues}
          isProtrusion={true}
          roofOrProtrusionName={getMicroCopy(
            microCopy.RESULTS_SELECTED_PROTRUSION_NAME,
            {
              name: protrusion.name
            }
          )}
          Illustration={protrusion.illustration}
        />
      ) : undefined}
    </>
  );
};

const DimensionsSection = ({
  title,
  roofOrProtrusionName,
  lengthValues,
  angleValues,
  Illustration,
  isProtrusion
}: {
  title: string;
  roofOrProtrusionName: string;
  lengthValues: Record<string, string>;
  angleValues: Record<string, string>;
  Illustration: SVGImport;
  isProtrusion?: boolean;
}) => {
  const { getMicroCopy } = useSiteContext();
  const dataTestIDPrefix = `selected-${isProtrusion ? "protrusion" : "roof"}`;

  return (
    <>
      <SectionTitle
        className={isProtrusion ? classes.topPadding : undefined}
        data-testid={`${dataTestIDPrefix}-title`}
      >
        {title}
      </SectionTitle>
      <Container data-testid={`${dataTestIDPrefix}-information-section`}>
        <RoofIllustrationBlock>
          <Illustration
            className={classes.roofIllustration}
            data-testid={`${dataTestIDPrefix}-illustration`}
          />
          <RoofName data-testid={`${dataTestIDPrefix}-name`} variant="h6">
            {roofOrProtrusionName}
          </RoofName>
        </RoofIllustrationBlock>
        <RoofDimensionsBlock>
          {Object.entries(lengthValues).map(([key, value]) => (
            <Dimension
              key={key}
              label={`${getMicroCopy(
                microCopy.RESULTS_ROOF_SIDE_LABEL
              )} ${key}:`}
              value={value}
              dataTestIDPrefix={dataTestIDPrefix}
            />
          ))}

          {Object.entries(angleValues).map(([key, value], index) => (
            <Dimension
              key={key}
              label={`${getMicroCopy(
                microCopy.RESULTS_ROOF_ANGLE_LABEL
              )} ${key}:`}
              value={value}
              breakRow={index === 0}
              dataTestIDPrefix={dataTestIDPrefix}
            />
          ))}
        </RoofDimensionsBlock>
      </Container>
    </>
  );
};

const Dimension = ({
  label,
  value,
  dataTestIDPrefix,
  breakRow
}: {
  label: string;
  value: string;
  dataTestIDPrefix: string;
  breakRow?: boolean;
}) => (
  <DimensionWrapper
    data-testid={`${dataTestIDPrefix}-dimension`}
    className={breakRow ? classes.breakRow : undefined}
  >
    <DimensionLabel data-testid={`${dataTestIDPrefix}-dimension-label`}>
      {label}
    </DimensionLabel>
    <DimensionValue data-testid={`${dataTestIDPrefix}-dimension-value`}>
      {value}
    </DimensionValue>
  </DimensionWrapper>
);

export default SelectedRoof;
